import React from 'react';
import { CheckCircle, Target, Award, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Mission-Driven',
      description: 'Empowering businesses with reliable, scalable IT infrastructure.',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Excellence',
      description: 'Committed to delivering the highest quality IT solutions and support.',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Client-Focused',
      description: 'Your success is our priority. We build lasting partnerships.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">About CyberTek Technology</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional IT services and network architecture solutions designed for growing businesses across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg"
                alt="Technology"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                CyberTek Technology was founded with a simple mission: to provide small and mid-sized businesses with enterprise-level IT infrastructure and support at affordable prices.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We understand that reliable technology is the backbone of modern business. Our team of experienced professionals specializes in designing, implementing, and maintaining office networks that are secure, scalable, and tailored to your specific needs.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                From new office buildouts to network upgrades and ongoing support, we're your trusted IT partner every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600">What drives us to deliver exceptional IT services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="p-8 bg-white rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-[#2995FF] mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">What We Do</h2>
              <p className="text-lg text-gray-600">
                CyberTek Technology provides comprehensive IT services tailored to your business:
              </p>
              <div className="space-y-4">
                {[
                  'Office Network Architecture & Implementation',
                  'Secure Wi-Fi Design & Optimization',
                  'System Administration & End-User Support',
                  'Microsoft 365 Setup, Migration & Support',
                  'Cloud & Hybrid Infrastructure Planning',
                  'Endpoint Protection & Cybersecurity Services',
                  'IT Consulting for New Office Builds',
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-[#2995FF] mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg"
                alt="Network Infrastructure"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2995FF] to-[#004AAD]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Let's Work Together</h2>
          <p className="text-xl mb-8 text-blue-100">
            Ready to see how CyberTek can transform your IT infrastructure? Get in touch with us today.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-[#2995FF] rounded-lg font-semibold hover:bg-gray-100 shadow-lg"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
